import React, { useState } from "react";
import {
  User,
  Lock,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../../utils/axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(true);

  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLogin = async () => {
    if (!email || !password) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please enter username and password.",
      });
      return;
    }

    setLoading(true);

    try {
      const res = await api.post("/login", {
        email,
        password,
      });

      if (res.data.token) {
        localStorage.setItem("token", res.data.token);

        login(
          res.data.data.email,
          res.data.data.username,
          res.data.data.role,
          res.data.data.available_credit,
          res.data.data.id,

        );

        Swal.fire({
          icon: "success",
          title: "Login Successful",
          text: "Welcome back!",
          timer: 1500,
          showConfirmButton: false,
        });

        navigate("/");
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text:
          error.response?.data?.message ||
          "Invalid credentials",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f1f2f6] flex flex-col items-center justify-center px-4">

      {/* Title Outside Box */}
      <h1 className="text-2xl font-light text-gray-600 mb-8">
        Back Office
      </h1>

      {/* Login Box */}
      <div className="w-full max-w-sm bg-white rounded shadow-md p-8">

        {/* Login Label */}
        <div className="text-center text-gray-600 text-sm mb-5">
          Login Account
        </div>

        {/* Username */}
        <div className="relative mb-4">
          <input
            type="text"
            placeholder="Username"
            className="w-full border border-gray-300 rounded px-4 py-3 pr-10 focus:outline-none focus:border-blue-500"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <User
            size={18}
            className="absolute right-3 top-3.5 text-gray-400"
          />
        </div>

        {/* Password */}
        <div className="relative mb-4">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            className="w-full border border-gray-300 rounded px-4 py-3 pr-10 focus:outline-none focus:border-blue-500"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-3 text-gray-400"
          >
            {showPassword ? (
              <EyeOff size={18} />
            ) : (
              <Eye size={18} />
            )}
          </button>
        </div>

        {/* Remember + Login */}
        <div className="flex items-center justify-between mb-5">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={remember}
              onChange={() => setRemember(!remember)}
              className="accent-blue-600"
            />
            Remember Me
          </label>

          <button
            onClick={handleLogin}
            disabled={loading}
            className={`bg-blue-600 hover:bg-blue-700 text-white px-8 py-2 rounded transition flex items-center gap-2
          ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
          >
            {loading ? (
              <>
                <Loader2
                  size={16}
                  className="animate-spin"
                />
                Loading
              </>
            ) : (
              "Login"
            )}
          </button>
        </div>

        {/* Language */}
        <div className="text-center text-sm text-gray-500 mb-6">
          🇺🇸 English
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 leading-6">
          It is recommended to use Chrome browser
          for the best experience
        </p>
      </div>
    </div>
  );
};

export default LoginPage;